import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutocompleteElement from "@atoms/autocomplete";
import AutocompleteDynamic from "@atoms/autocompleteDynamic";
import Datepicker from "@atoms/datepicker";
import dayjs from "@helpers/dayjs";
import { useIsTablet } from "@hooks/use-is-tablet";
import {
  autocompleteFieldConfig,
  getDestinationLabel,
  getPackingEditFieldGroups,
  PackingEditField,
  staticAutocompleteFieldConfig,
} from "../../helpers/packing-edit-fields";
import { AutocompleteFieldKey, StaticAutocompleteFieldKey } from "../../types";

type PackingListEditFormProps = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  data: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  isContainer: boolean;
  readOnly?: boolean;
  /** When set, only these packing field keys are shown (e.g. Lite WMS tablet). */
  editFieldKeys?: readonly string[];
};

const mapToOption = (value: { id: number; name: string } | null) =>
  value ? { id: value.id, label: value.name } : null;

const PackingListEditForm: React.FC<PackingListEditFormProps> = ({
  open,
  onClose,
  onSave,
  data,
  onChange,
  isContainer,
  readOnly = false,
  editFieldKeys,
}) => {
  const isTablet = useIsTablet();
  const groups = getPackingEditFieldGroups(editFieldKeys);
  const useCompactLayout = isTablet && !!editFieldKeys;
  const formTitle = readOnly
    ? "View Packing List Item"
    : "Edit Packing List Item";

  const renderField = (field: PackingEditField) => {
    const value = data[field.cargoKey];

    if (field.fieldKey === "NUMBER" && isContainer) {
      return null;
    }

    if (field.type === "date") {
      return (
        <Datepicker
          key={field.cargoKey}
          label={field.label}
          start={value ? new Date(value) : null}
          selectsRange={false}
          onChangeCallback={(selected: string | null) =>
            onChange({
              [field.cargoKey]: selected
                ? dayjs(selected).endOf("day").toISOString()
                : "",
            })
          }
          size="small"
          className="priceExpiration"
          portalId="packing-edit-datepicker-portal"
          disabled={readOnly}
        />
      );
    }

    if (field.type === "number") {
      return (
        <TextField
          key={field.cargoKey}
          label={field.label}
          size="small"
          fullWidth
          disabled={readOnly}
          value={value ?? ""}
          onChange={(e) => {
            const input = e.target.value
              .replace(/[^0-9.,]/g, "")
              .replace(",", ".");
            const num = parseFloat(input);
            onChange({
              [field.cargoKey]: input === "" || Number.isNaN(num) ? null : num,
            });
          }}
        />
      );
    }

    if (field.type === "dynamicAutocomplete") {
      const config =
        autocompleteFieldConfig[field.fieldKey as AutocompleteFieldKey];

      if (field.fieldKey === "DESTINATION") {
        return (
          <AutocompleteDynamic
            key={field.cargoKey}
            label={field.label}
            multi={false}
            size="small"
            disabled={readOnly}
            queryParam={config.query}
            initial={
              value
                ? {
                    id: value?.id,
                    label: getDestinationLabel(value) ?? "",
                  }
                : null
            }
            updateFields={(selected: any) =>
              onChange({ [config.field]: selected || null })
            }
            optionMapper={(raw) => ({
              id: raw.id,
              label: [
                raw.customer?.name,
                raw.delivery_city?.name,
                raw.delivery_address,
              ]
                .filter(Boolean)
                .join(", "),
            })}
            endpoint={config.endpoint}
          />
        );
      }

      return (
        <AutocompleteDynamic
          key={field.cargoKey}
          label={field.label}
          multi={false}
          size="small"
          disabled={readOnly}
          queryParam={config.query}
          initial={mapToOption(value || null)}
          updateFields={(selected: any) =>
            onChange({ [config.field]: selected || null })
          }
          endpoint={config.endpoint}
        />
      );
    }

    if (field.type === "staticAutocomplete") {
      const config =
        staticAutocompleteFieldConfig[
          field.fieldKey as StaticAutocompleteFieldKey
        ];
      const options = config.options.map((opt: string) => ({
        id: opt,
        label: opt,
      }));

      return (
        <AutocompleteElement
          key={field.cargoKey}
          label={field.label}
          options={options}
          multi={false}
          size="small"
          disabled={readOnly}
          value={options.find((o) => o.id === value) ?? value ?? null}
          onChange={(_: any, selected: { id: string } | null) =>
            onChange({ [config.field]: selected?.id ?? null })
          }
        />
      );
    }

    return (
      <TextField
        key={field.cargoKey}
        label={field.label}
        size="small"
        fullWidth
        disabled={readOnly}
        value={value ?? ""}
        onChange={(e) => onChange({ [field.cargoKey]: e.target.value || null })}
      />
    );
  };

  const formBody = (
    <Box sx={{ p: 2 }}>
      <Box id="packing-edit-datepicker-portal" />
      {isTablet && (
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          {formTitle}
        </Typography>
      )}
      <Stack
        spacing={useCompactLayout ? 1.5 : 2.5}
        sx={{ maxHeight: isTablet ? "70vh" : "65vh", overflow: "auto" }}
      >
        {useCompactLayout ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1.5,
            }}
          >
            {groups.flatMap((group) =>
              group.fields.map((field) => renderField(field)),
            )}
          </Box>
        ) : (
          groups.map((group) => (
            <Box key={group.title}>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: group.color,
                  fontWeight: 600,
                }}
              >
                {group.title}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {group.fields.map((field) => renderField(field))}
              </Box>
            </Box>
          ))
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ mt: 2 }}
      >
        <Button onClick={onClose}>{readOnly ? "Close" : "Cancel"}</Button>
        {!readOnly && (
          <Button variant="contained" onClick={onSave}>
            Save
          </Button>
        )}
      </Stack>
    </Box>
  );

  if (isTablet) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          },
        }}
      >
        {open && formBody}
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{formTitle}</DialogTitle>
      <DialogContent dividers>{formBody}</DialogContent>
    </Dialog>
  );
};

export default PackingListEditForm;
